/**
 * M16A — UserHomeService
 *
 * Resolve o dashboard/home apropriado para o usuário autenticado com base
 * em seu perfil real (role) e permissões efetivas, sem depender de activeClientId.
 *
 * Regras:
 * - INSPECTOR → Dashboard do Inspetor
 * - ENGINEER → Central de Trabalho da Engenharia
 * - MANAGER → Central Gerencial
 * - ADMIN → Central Administrativa
 * - DEVELOPER → Central Técnica do Sistema
 * - Outros → Home genérica baseada em permissões
 */

import type { RequestContext } from "../context/request-context.js";
import { PermissionCode, RoleCode } from "../../domain/identity/access-types.js";
import type { Professional } from "../../domain/identity/professional.js";
import type { ProfessionalRepository } from "../../repositories/identity-repositories.js";
import type { FieldControlScope } from "./field-control-service.js";
import { summarizeFieldProgress } from "../../domain/field-control/field-inspection-progress.js";

export type HomeProfile =
  | "INSPECTOR"
  | "ENGINEER"
  | "MANAGER"
  | "ADMIN"
  | "DEVELOPER"
  | "GENERIC";

export interface UserHomeContext {
  profile: HomeProfile;
  userId: string;
  userName: string;
  roleDisplay: string;
  professionalId: string | null;
  permissions: string[];
  canViewIndicators: boolean;
}

/** Dados operacionais do Inspetor para a Home (M16A HOME-FINAL-01). */
export interface InspectorDashboardData {
  professional: Professional | null;
  reportCounts: { total: number; inElaboration: number; completed: number };
  fieldScope: FieldControlScope | null;
  subOsList: Array<{ id: string; number: string; status: string; projectName: string | null; clientName: string | null }>;
}

/**
 * Extensão do RequestContext com campos opcionais de UI que podem ser
 * injetados pela camada de apresentação (session/auth) mas não fazem
 * parte do contrato de domínio.
 */
interface RichRequestContext extends RequestContext {
  readonly userName?: string;
  readonly roleDisplay?: string;
  readonly role?: string;
}

export class UserHomeService {
  constructor(
    private readonly professionalRepo?: Pick<ProfessionalRepository, "findById">,
    private readonly reportRepo?: { countByInspector?(context: RequestContext, inspectorProfessionalId: string, authorizedClientIds: readonly string[]): Promise<{ total: number; inElaboration: number; completed: number }> },
    private readonly fieldControlService?: { listMyLines(context: RequestContext): Promise<FieldControlScope> },
    private readonly rdoService?: { listSubOsForProfessional(context: RequestContext, professionalId: string): Promise<Array<{ subOs: any; context: any }>> },
    private readonly listAuthorizedClientIds?: (context: RequestContext) => Promise<readonly string[]>
  ) {}

  /**
   * Resolve o contexto da Home para o usuário da sessão.
   * Usa dados do RequestContext (origem servidor), nunca do browser.
   */
  public load(context: RichRequestContext): UserHomeContext {
    const userId = context.userId || "";
    const userName = context.userName || "Usuário";
    const role = (context.role || "").toUpperCase();
    const roleDisplay = context.roleDisplay || role || "USUÁRIO";
    const professionalId = context.professionalId || null;
    const permissions = context.permissions ? Array.from(context.permissions) : [];

    const profile = this.resolveProfile(role);
    const canViewIndicators = permissions.includes(PermissionCode.DashboardIndicatorsView);

    return {
      profile,
      userId,
      userName,
      roleDisplay,
      professionalId,
      permissions,
      canViewIndicators
    };
  }

  /**
   * M16A HOME-FINAL-01: carrega dados operacionais reais do Inspetor.
   * Chamado separadamente após load() para evitar sobrecarregar perfis
   * que não precisam desses dados (Engineer, Manager, etc.).
   */
  public async loadInspectorData(context: RequestContext): Promise<InspectorDashboardData> {
    const professionalId = context.professionalId ? String(context.professionalId) : null;
    // Não transformar falta de vínculo ou erro de leitura em métricas zero.
    if (!professionalId) {
      throw new Error("Não foi possível identificar o cadastro profissional do Inspetor.");
    }
    if (!this.reportRepo?.countByInspector || !this.listAuthorizedClientIds) {
      throw new Error("Consulta autorizada de relatórios indisponível.");
    }
    // O usuário autenticado é resolvido pelo servidor. O escopo de Clientes
    // deve vir da mesma regra oficial de autorização usada pelos cadastros.
    const authorizedClientIds = await this.listAuthorizedClientIds(context);

    // Escopar TAMBÉM Linhas e Sub-OS por Cliente autorizado: consultar sem
    // clientId poderia revelar atribuições de Clientes sem acesso atual.
    const clientContexts = authorizedClientIds.map(clientId => ({
      ...context, activeClientId: clientId as any
    }));
    const [professional, reportCounts, fieldScopes, subOsGroups] = await Promise.all([
      this.professionalRepo
        ? this.professionalRepo.findById(context, professionalId as any)
        : Promise.resolve(null),
      this.reportRepo.countByInspector(context, professionalId, authorizedClientIds),
      this.fieldControlService
        ? Promise.all(clientContexts.map(scoped => this.fieldControlService!.listMyLines(scoped)))
        : Promise.resolve([] as FieldControlScope[]),
      this.rdoService
        ? Promise.all(clientContexts.map(scoped => this.rdoService!.listSubOsForProfessional(scoped, professionalId)))
        : Promise.resolve([])
    ]);

    const rowByKey = new Map(
      fieldScopes.flatMap(scope => scope.rows)
        .map(row => [`${row.progress.clientId}:${row.lineEquipmentId}`, row] as const)
    );
    const rows = Array.from(rowByKey.values());
    const systemById = new Map(
      fieldScopes.flatMap(scope => scope.systems).map(system => [system.id, system] as const)
    );
    const fieldScope: FieldControlScope | null = this.fieldControlService
      ? {
          rows,
          summary: summarizeFieldProgress(rows.map(row => row.progress)),
          systems: Array.from(systemById.values())
        }
      : null;

    // C07: Home do Inspetor mostra Sub-OS cuja responsabilidade principal
    // está no cadastro SubWorkOrder.professionalId, não colaboradores do RDO.
    const subOsRaw = subOsGroups.flat().filter(item =>
      String(item.subOs?.professionalId ?? "") === professionalId
    );
    const subOsList = subOsRaw.map((item: { subOs: any; context: any }) => ({
      id: item.subOs?.id || "",
      number: item.subOs?.number || item.subOs?.subOsNumber || "",
      status: item.subOs?.status || "",
      projectName: item.context?.projectName || null,
      clientName: item.context?.clientName || null
    }));

    return {
      professional,
      reportCounts,
      fieldScope,
      subOsList
    };
  }

  private resolveProfile(role: string): HomeProfile {
    switch (role) {
      case RoleCode.Inspector:
        return "INSPECTOR";
      case RoleCode.Engineer:
        return "ENGINEER";
      case RoleCode.Manager:
        return "MANAGER";
      case RoleCode.Admin:
        return "ADMIN";
      case RoleCode.Developer:
        return "DEVELOPER";
      default:
        return "GENERIC";
    }
  }
}